import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Loader2, Save, SendHorizonal } from 'lucide-react';

interface StepShellProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  onSaveDraft?: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  isFinal?: boolean;
  isFirst?: boolean;
  saving?: boolean;
  showDraft?: boolean;
}

export function StepShell({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  onSaveDraft,
  onSubmit,
  submitLabel = 'Submit for Review',
  isFinal,
  isFirst,
  saving,
  showDraft = true,
}: StepShellProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-display">{title}</CardTitle>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>

      <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-t bg-muted/30 rounded-b-lg">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={isFirst}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>

        <div className="flex flex-wrap gap-2">
          {showDraft && onSaveDraft && (
            <Button type="button" variant="outline" onClick={onSaveDraft} disabled={saving} className="gap-1">
              <Save className="h-4 w-4" /> Save Draft
            </Button>
          )}
          {isFinal ? (
            <Button type="button" onClick={onSubmit} disabled={saving} className="gap-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
              {submitLabel}
            </Button>
          ) : (
            <Button type="button" onClick={onNext} className="gap-1">
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
